import { type CSSProperties, type RefObject, useEffect, useMemo } from 'react';

import {
  autoUpdate,
  FloatingPortal,
  type Middleware,
  shift,
  useFloating,
  type VirtualElement,
} from '@floating-ui/react';
import classNames from 'classnames';

import { getBackgroundColorStyle } from '../../../../utils/background.js';
import { cn } from '../../../../utils/cn.js';
import { computeColor } from '../../../../utils/colors.js';
import { computeFont } from '../../../../utils/fonts.js';
import { getFontSizeVariables } from '../../../../utils/getFontSizeVariables.js';

import CaretDown from '../../../../icon/CaretDown/index.js';
import * as styles from '../../../Navbar.css';
import { NAVBAR_MENU_ITEM_BORDER_DEFAULTS, NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2 } from '../../NavbarMenuItem.constants.js';
import type { NavbarMenuItemV2ElementProps, TNavbarItemV2ButtonElement } from '../../NavbarMenuItem.type.js';
import { DropdownContent } from './DropdownContent.js';

interface FloatingDropdownProps extends NavbarMenuItemV2ElementProps {
  controlbyselected: boolean | string;
  labelComponent?: React.ReactNode;
  showCaret?: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  shouldForceOpen: boolean;
  isInEditor: boolean;
  middlewares?: Middleware[];
  dropdownNodeHeight?: number;
  evaluatedHoverBorderRadius?: string | number;
  excludeSeparator?: boolean;
  showOnClick?: boolean;
  virtualElementRef?: RefObject<VirtualElement | undefined>;
}

export function FloatingDropdown({
  element,
  className,
  children,
  customAttributes,
  labelComponent,
  showCaret = true,
  isOpen,
  setIsOpen,
  shouldForceOpen,
  isInEditor,
  attributes,
  middlewares = [],
  evaluatedHoverBorderRadius,
  excludeSeparator,
  screen,
  showOnClick,
  virtualElementRef,
  forceShowAccordion, // we don't want this in the ...props rest since we're spreading it on a div
  ...props
}: FloatingDropdownProps) {
  const {
    label,
    padding,
    background,
    color,
    borderColor,
    borderWidth,
    borderStyle,
    borderRadius,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverTextColor,
    hoverBackgroundColor,
    mobileColor,
    mobilePadding,
    mobileFontSize,
  } = element.attrs || {};

  let fontFamily: CSSProperties['fontFamily'] | undefined;
  let fontSize: CSSProperties['fontSize'] | undefined;
  let fontWeight: CSSProperties['fontWeight'] | undefined;
  let letterSpacing: CSSProperties['letterSpacing'] | undefined;
  if (element.attrs?.type !== 'logo') {
    fontFamily = element.attrs?.fontFamily;
    fontSize = element.attrs?.fontSize;
    fontWeight = element.attrs?.fontWeight;
    letterSpacing = (element.attrs as TNavbarItemV2ButtonElement['attrs'])?.letterSpacing;
  }

  const openDropdown = () => {
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const isMobile = screen === 'mobile';

  const { x, y, refs, strategy } = useFloating({
    open: isOpen,
    strategy: 'fixed',
    placement: isMobile ? 'bottom-start' : 'bottom',
    middleware: [shift(), ...middlewares],
    ...(isInEditor || !isMobile
      ? { whileElementsMounted: autoUpdate }
      : {
          whileElementsMounted: (ref, floater, update) =>
            autoUpdate(ref, floater, update, {
              ancestorResize: true,
              ancestorScroll: true,
              animationFrame: true,
            }),
        }),
  });

  useEffect(() => {
    if (!isInEditor) return;

    const setPositionReference = () => {
      if (virtualElementRef?.current) {
        refs.setPositionReference(virtualElementRef.current);
      }
    };

    setPositionReference();

    const id = setInterval(setPositionReference, 100);

    return () => {
      clearInterval(id);
    };
  }, [isInEditor, virtualElementRef, refs.setPositionReference]);

  const [firstChild, restChildren]: [React.ReactNode, React.ReactNode] = useMemo(
    () => (Array.isArray(children) ? [children[0], children.slice(1)] : [null, null]),
    [children],
  );

  const RenderedDropdownReactNode = useMemo(() => {
    if (!children) return null;

    const zIndex = isOpen ? 9999 : -9999;

    if (isInEditor) {
      return (
        <div
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y,
            left: x ?? 0,
            paddingTop: y ? 4 : 0,
            zIndex,
          }}
        >
          <DropdownContent open={shouldForceOpen}>{children}</DropdownContent>
        </div>
      );
    }

    const floatingStyles = {
      position: strategy,
      top: y ?? 0,
      left: x ?? 0,
      zIndex,
      paddingTop: y ? 4 : 0,
    };

    // Case of user button
    if (element?.content?.length === 1 && element.content[0].type === 'navbar_dropdown') {
      return (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              opacity: isOpen ? 1 : 0,
              pointerEvents: isOpen ? 'auto' : 'none',
              transition: 'opacity 120ms ease',
            }}
          >
            <DropdownContent open={isOpen}>{firstChild}</DropdownContent>
          </div>
        </FloatingPortal>
      );
    }

    if (!restChildren || (Array.isArray(restChildren) && restChildren.length === 0)) {
      return null;
    }

    return (
      <FloatingPortal>
        <div ref={refs.setFloating} style={floatingStyles}>
          <DropdownContent open={isOpen}>{restChildren}</DropdownContent>
        </div>
      </FloatingPortal>
    );
  }, [
    x,
    y,
    restChildren,
    isInEditor,
    refs.setFloating,
    shouldForceOpen,
    strategy,
    children,
    isOpen,
    element?.content,
    firstChild,
  ]);

  const evaluatedLabel = labelComponent || firstChild || (isInEditor ? null : label);

  return (
    <div
      className={cn(styles.navbarItemLinkContainerV2)}
      {...attributes}
      onMouseEnter={!showOnClick ? openDropdown : undefined}
      onMouseOver={!showOnClick ? openDropdown : undefined}
      onFocus={!showOnClick ? openDropdown : undefined}
      onMouseLeave={!showOnClick ? closeDropdown : undefined}
      onClick={showOnClick ? toggleOpen : undefined}
      onKeyDown={
        showOnClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleOpen();
              }
            }
          : undefined
      }
    >
      {/* spreading props on standard DOM elements causes React warnings and hydration errors */}
      <div className="flex h-fit w-fit" {...(evaluatedLabel && { ref: refs.setReference })}>
        {(evaluatedLabel || showCaret) && (
          <div
            {...props}
            role="presentation"
            {...customAttributes}
            className={classNames(
              styles.navbarItemDropdownV2,
              className,
              customAttributes?.className,
              isInEditor && 'flex items-center',
            )}
            style={
              {
                '--padding': padding ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.padding,
                '--border-radius': borderRadius ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
                '--border-color': borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
                '--border-style': borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
                '--border-width': borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
                ...getBackgroundColorStyle(background ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.background),
                '--text-color': computeColor(color ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color),
                '--font-family': computeFont(fontFamily) || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.fontFamily,
                '--letter-spacing': letterSpacing ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.letterSpacing,
                ...getFontSizeVariables(fontSize ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.fontSize, mobileFontSize),
                '--font-weight': fontWeight ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.fontWeight,

                '--hover-text-color': hoverTextColor || color || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color,
                '--hover-background-color':
                  hoverBackgroundColor || background || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.background,
                '--hover-border-radius': evaluatedHoverBorderRadius,
                '--hover-border-color': hoverBorderColor || borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
                '--hover-border-style': hoverBorderStyle || borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
                '--hover-border-width': hoverBorderWidth || borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
                '--mobile-text-color': computeColor(mobileColor || color || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color),
                '--mobile-padding': mobilePadding || padding || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.padding,
              } as CSSProperties
            }
          >
            {evaluatedLabel}
            {showCaret && <CaretDown size={16} weight="bold" className="text-wb-secondary" />}
          </div>
        )}
      </div>
      {!excludeSeparator && <div className={styles.navbarItemSeparatorV2} />}
      {RenderedDropdownReactNode}
    </div>
  );
}
